(() => {
  const VERSION = '2026.08.21.3';
  const log = window.BHCardModalLog || ((level, eventName, details = {}) => {
    const entry = { time: new Date().toISOString(), event: eventName, version: VERSION, ...details };
    window.__BH_CARD_MODAL_LOGS__ = window.__BH_CARD_MODAL_LOGS__ || [];
    window.__BH_CARD_MODAL_LOGS__.push(entry);
    const method = typeof console[level] === 'function' ? level : 'log';
    console[method]('[BH Card Modal]', eventName, entry);
    return entry;
  });

  const previousState = window.BHCardProduct;
  if (previousState?.version === VERSION) {
    log('info', 'asset:already-current');
    return;
  }
  if (previousState?.controller) {
    previousState.controller.abort();
    log('info', 'asset:stale-listeners-aborted', { previousVersion: previousState.version || null });
  }

  const controller = new AbortController();
  const state = {
    initialized: true,
    version: VERSION,
    controller,
    cache: new WeakMap()
  };
  window.BHCardProduct = state;

  const rootUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  function productData(dialog) {
    if (state.cache.has(dialog)) return state.cache.get(dialog);
    const script = dialog.querySelector('[data-bh-card-product-json]');
    let data = { variants: [], quantityRules: {}, moneyFormat: '${{amount}}' };
    try {
      data = JSON.parse(script?.textContent || '{}');
    } catch (error) {
      console.error('[BH Card] No se pudo leer el producto.', error);
    }
    state.cache.set(dialog, data);
    return data;
  }

  function formatMoney(cents, format = '${{amount}}') {
    const value = Number(cents || 0);
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount';
    const formats = {
      amount: [2, '.', ','],
      amount_no_decimals: [0, '.', ','],
      amount_with_comma_separator: [2, ',', '.'],
      amount_no_decimals_with_comma_separator: [0, ',', '.'],
      amount_with_apostrophe_separator: [2, '.', "'"]
    };
    const [precision, decimal, thousands] = formats[placeholder] || formats.amount;
    const fixed = (value / 100).toFixed(precision);
    const [integer, fraction] = fixed.split('.');
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const money = precision ? `${grouped}${decimal}${fraction}` : grouped;
    return format.replace(/\{\{\s*\w+\s*\}\}/, money);
  }

  function selectedOptions(dialog) {
    return [...dialog.querySelectorAll('[data-bh-card-option]')].map(group =>
      group.querySelector('[data-bh-card-option-value].is-selected')?.dataset.value || ''
    );
  }

  function selectedVariant(dialog) {
    const selections = selectedOptions(dialog);
    return productData(dialog).variants.find(variant =>
      variant.options.every((option, index) => option === selections[index])
    ) || null;
  }

  function setError(dialog, message = '') {
    const element = dialog.querySelector('[data-bh-card-error]');
    if (!element) return;
    element.textContent = message;
    element.hidden = !message;
  }

  function syncDialog(dialog) {
    const data = productData(dialog);
    const variant = selectedVariant(dialog);
    const selections = selectedOptions(dialog);
    const addButton = dialog.querySelector('[data-bh-card-add]');
    const addLabel = dialog.querySelector('[data-bh-card-add-label]');
    const price = dialog.querySelector('[data-bh-card-modal-price]');
    const image = dialog.querySelector('[data-bh-card-modal-image]');
    const quantityInput = dialog.querySelector('[data-bh-card-quantity]');

    dialog.querySelectorAll('[data-bh-card-option-value]').forEach(button => {
      const group = button.closest('[data-bh-card-option]');
      const position = Number(group?.dataset.optionPosition || 0);
      const candidate = [...selections];
      candidate[position] = button.dataset.value;
      const possible = data.variants.some(item =>
        item.available && item.options.every((option, index) => option === candidate[index])
      );
      button.classList.toggle('is-unavailable', !possible);
      button.setAttribute('aria-disabled', possible ? 'false' : 'true');
    });

    if (price && variant) price.textContent = formatMoney(variant.price, data.moneyFormat);
    if (image && variant?.featured_image?.src) image.src = variant.featured_image.src;
    if (quantityInput && variant) {
      const rule = data.quantityRules?.[String(variant.id)] || { min: 1, max: null, step: 1 };
      quantityInput.min = String(rule.min || 1);
      quantityInput.step = String(rule.step || 1);
      if (rule.max == null) quantityInput.removeAttribute('max');
      else quantityInput.max = String(rule.max);
      clampQuantity(quantityInput, quantityInput.value);
    }

    const available = Boolean(variant?.available);
    if (addButton) addButton.disabled = !available;
    if (addLabel) {
      addLabel.textContent = !variant
        ? 'Combinación no disponible'
        : available
          ? `Agregar · ${formatMoney(variant.price, data.moneyFormat)}`
          : 'Agotado';
    }
    dialog.dataset.variantId = variant?.id || '';
    setError(dialog);
  }

  function clampQuantity(input, nextValue) {
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const step = Number(input.step) || 1;
    const numeric = Number.isFinite(Number(nextValue)) ? Number(nextValue) : min;
    const stepped = min + Math.round((numeric - min) / step) * step;
    input.value = String(Math.min(max, Math.max(min, stepped)));
  }

  async function refreshCartCount() {
    try {
      const response = await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(element => {
        element.textContent = String(cart.item_count);
      });
    } catch (error) {
      console.warn('[BH Card] No se pudo actualizar el contador.', error);
    }
  }

  async function addToCart(dialog, trigger) {
    const variant = selectedVariant(dialog);
    const quantityInput = dialog.querySelector('[data-bh-card-quantity]');
    const addButton = dialog.querySelector('[data-bh-card-add]');
    const addLabel = dialog.querySelector('[data-bh-card-add-label]');
    if (!variant?.available || !quantityInput || !addButton) return;

    clampQuantity(quantityInput, quantityInput.value);
    const cartSurface = document.querySelector('cart-notification, cart-drawer');
    const sections = typeof cartSurface?.getSectionsToRender === 'function'
      ? cartSurface.getSectionsToRender().map(section => section.id)
      : [];
    const formData = new FormData();
    formData.append('id', String(variant.id));
    formData.append('quantity', String(Number(quantityInput.value)));
    if (sections.length) formData.append('sections', sections.join(','));
    formData.append('sections_url', window.location.pathname);

    addButton.disabled = true;
    addButton.classList.add('is-loading');
    if (addLabel) addLabel.textContent = 'Agregando…';
    setError(dialog);

    try {
      const response = await fetch(`${rootUrl()}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || 'No pudimos agregar el producto.');

      if (cartSurface && result.sections && typeof cartSurface.renderContents === 'function') {
        cartSurface.renderContents(result);
      } else {
        await refreshCartCount();
      }

      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { source: 'bh-card-product', variant, quantity: Number(quantityInput.value), result }
      }));

      dialog.close();
      trigger?.classList.add('is-added');
      window.setTimeout(() => trigger?.classList.remove('is-added'), 1600);
    } catch (error) {
      setError(dialog, error.message || 'No pudimos agregar el producto. Intenta nuevamente.');
      addButton.disabled = false;
      if (addLabel) addLabel.textContent = 'Intentar nuevamente';
    } finally {
      addButton.classList.remove('is-loading');
    }
  }

  document.addEventListener('click', event => {
    if (event.__bhCardHandled) return;
    const opener = event.target.closest('[data-bh-card-modal-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('aria-controls'));
      log('info', 'asset:button-click', {
        dialogId: opener.getAttribute('aria-controls'),
        dialogFound: Boolean(dialog),
        modalApiFound: Boolean(window.BHModal),
        openProductAvailable: typeof window.BHModal?.openProduct === 'function'
      });
      if (!dialog) {
        log('error', 'asset:fallback-dialog-missing');
        return;
      }
      dialog._bhTrigger = opener;

      if (typeof window.BHModal?.openProduct === 'function') {
        const data = productData(dialog);
        log('info', 'asset:using-shared-modal', {
          productId: data.id || null,
          variants: data.variants?.length || 0
        });
        window.BHModal.openProduct(data);
        return;
      }

      if (typeof dialog.showModal !== 'function') {
        log('error', 'asset:dialog-api-unavailable');
        return;
      }
      try {
        syncDialog(dialog);
        dialog.showModal();
        document.documentElement.classList.add('bh-card-modal-open');
        log('info', 'asset:fallback-dialog-opened', { productId: productData(dialog).id || null });
      } catch (error) {
        log('error', 'asset:fallback-dialog-open-failed', {
          message: error?.message || String(error),
          stack: error?.stack || null
        });
      }
      return;
    }

    const close = event.target.closest('[data-bh-card-modal-close]');
    if (close) {
      close.closest('[data-bh-card-modal]')?.close();
      return;
    }

    const option = event.target.closest('[data-bh-card-option-value]');
    if (option && option.getAttribute('aria-disabled') !== 'true') {
      const dialog = option.closest('[data-bh-card-modal]');
      const group = option.closest('[data-bh-card-option]');
      group.querySelectorAll('[data-bh-card-option-value]').forEach(button => {
        const selected = button === option;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      syncDialog(dialog);
      return;
    }

    const minus = event.target.closest('[data-bh-card-quantity-minus]');
    const plus = event.target.closest('[data-bh-card-quantity-plus]');
    if (minus || plus) {
      const dialog = event.target.closest('[data-bh-card-modal]');
      const input = dialog?.querySelector('[data-bh-card-quantity]');
      if (!input) return;
      const step = Number(input.step) || 1;
      clampQuantity(input, Number(input.value) + (plus ? step : -step));
      return;
    }

    const add = event.target.closest('[data-bh-card-add]');
    if (add) {
      const dialog = add.closest('[data-bh-card-modal]');
      addToCart(dialog, dialog?._bhTrigger);
    }
  }, { signal: controller.signal });

  document.addEventListener('change', event => {
    if (!event.target.matches('[data-bh-card-quantity]')) return;
    clampQuantity(event.target, event.target.value);
  }, { signal: controller.signal });

  document.addEventListener('click', event => {
    if (!event.target.matches('dialog.bh-card-modal')) return;
    const rect = event.target.getBoundingClientRect();
    const inside = event.clientX >= rect.left && event.clientX <= rect.right &&
      event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) event.target.close();
  }, { signal: controller.signal });

  document.addEventListener('close', event => {
    if (!event.target.matches?.('[data-bh-card-modal]')) return;
    document.documentElement.classList.remove('bh-card-modal-open');
    event.target._bhTrigger?.focus?.();
  }, { capture: true, signal: controller.signal });

  log('info', 'asset:initialized', {
    version: VERSION,
    buttons: document.querySelectorAll('[data-bh-card-modal-open]').length,
    dialogs: document.querySelectorAll('[data-bh-card-modal]').length,
    sharedModalApi: window.BHModal ? Object.keys(window.BHModal) : [],
    themeEditor: Boolean(window.Shopify?.designMode)
  });
})();
